import React, { useEffect, useState } from "react";
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButtons,
  IonBackButton,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonSpinner,
  IonList,
  IonItem,
  IonLabel,
  IonGrid,
  IonRow,
  IonCol,
  IonChip,
  IonAvatar,
  IonText,
  IonButton,
  IonIcon,
} from "@ionic/react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import {
  personOutline,
  calendarOutline,
  giftOutline,
  businessOutline,
  chevronForward,
  statsChart,
  people,
} from "ionicons/icons";
// import "leaflet/dist/leaflet.css";
import { baseUrl, config, requestID } from "../../../common/utils";
import axios from "axios";

const COLORS = [
  "#6366F1", // Modern indigo
  "#10B981", // Emerald
  "#F59E0B", // Amber
  "#EF4444", // Red
  "#8B5CF6", // Violet
  "#06B6D4", // Cyan
  "#F97316", // Orange
];

const AnalyticsDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [demographics, setDemographics] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [stats, setStats] = useState({
    totalBeneficiaries: 0,
    activeToday: 0,
    vouchersRedeemed: 0,
    totalCampaigns: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  useEffect(() => {
    getBeneficiaryStats();
  }, []);

  const getBeneficiaryStats = async () => {
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("requestID", requestID());
      const response = await axios.post(
        baseUrl() + "Beneficiarys/ss_beneficiary_stats",
        formData,
        config
      );

      const data = response.data.data || {};
      console.log("all data", data);

      // Convert API response object into PieChart-friendly format
      const chartData = [
        {
          name: "Campaigns",
          value: data.total_campaigns || 0,
          icon: businessOutline,
        },
        {
          name: "Beneficiaries",
          value: data.total_beneficiaries || 0,
          icon: people,
        },
        {
          name: "Vendors",
          value: data.total_vendors || 0,
          icon: personOutline,
        },
        {
          name: "Vouchers",
          value: data.total_vouchers || 0,
          icon: giftOutline,
        },
        {
          name: "Value Redeemed",
          value: data.total_value_redeemed || 0,
          icon: statsChart,
        },
        { name: "Money Redeemed", value: data.total_money_redeemed || 0 },
        { name: "Donated Campaign", value: data.total_donatedCampaign || 0 },
      ];

      setUsers(data.last_five_beneficiaries || []);
      setDemographics(chartData);

      // Update stats
      setStats({
        totalBeneficiaries: data.total_beneficiaries || 0,
        activeToday: data.active_today || 0,
        vouchersRedeemed: data.total_vouchers_redeemed || 0,
        totalCampaigns: data.total_campaigns || 0,
      });

      setLoading(false);
    } catch (err) {
      console.error("Error fetching stats", err);
      setLoading(false);
    }
  };

  // Pagination calculations
  const totalPages = Math.ceil(users.length / itemsPerPage);
  const paginatedUsers = users.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat().format(num);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <IonPage className = "pushDown">
      <IonHeader className="ion-no-border">
        <IonToolbar
          style={{
            "--background": "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          }}
        >
          <IonButtons slot="start">
          </IonButtons>
          <IonTitle style={{ "--color": "white", fontWeight: "600" }}>
            Analytics Dashboard
          </IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent className="ion-padding" fullscreen>
        {loading ? (
          <div className="ion-text-center" style={{ paddingTop: "4rem" }}>
            <IonSpinner
              name="crescent"
              style={{ width: "3rem", height: "3rem" }}
            />
            <IonText color="medium">
              <p style={{ marginTop: "1rem" }}>Loading analytics data...</p>
            </IonText>
          </div>
        ) : (
          <>
            <IonGrid>
              <IonRow>
                <IonCol size="12" sizeLg="6">
                  {/* System Stats Chart */}
                  <IonCard
                    style={{
                      borderRadius: "16px",
                      boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                    }}
                  >
                    <IonCardHeader style={{ paddingBottom: "0" }}>
                      <IonCardTitle
                        style={{ fontSize: "1.25rem", fontWeight: "600" }}
                      >
                        System Distribution
                      </IonCardTitle>
                    </IonCardHeader>
                    <IonCardContent
                      style={{ height: "400px", paddingTop: "0" }}
                    >
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={demographics}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={120}
                            dataKey="value"
                            label={({ name, value }) =>
                              `${name}: ${formatNumber(value)}`
                            }
                          >
                            {demographics.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={COLORS[index % COLORS.length]}
                                stroke="#fff"
                                strokeWidth={2}
                              />
                            ))}
                          </Pie>
                          <Tooltip
                            formatter={(value) => [
                              formatNumber(Number(value)),
                              "Count",
                            ]}
                          />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </IonCardContent>
                  </IonCard>
                </IonCol>
              </IonRow>
            </IonGrid>

            {/* Recent Beneficiaries */}
            <IonCard
              style={{
                borderRadius: "16px",
                boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
              }}
            >
              <IonCardHeader>
                <IonCardTitle
                  style={{ fontSize: "1.25rem", fontWeight: "600" }}
                >
                  Recent Beneficiaries
                </IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                <IonList lines="none">
                  {paginatedUsers.map((user, index) => (
                    <IonItem
                      key={user.id}
                      style={{
                        "--padding-start": "0",
                        "--inner-padding-end": "0",
                        "--background": "transparent",
                      }}
                    >
                      <div
                        style={{
                          width: "100%",
                          padding: "1rem",
                          background:
                            index % 2 === 0 ? "#f8fafc" : "transparent",
                          borderRadius: "12px",
                          marginBottom: "0.5rem",
                        }}
                      >
                        <IonGrid>
                          <IonRow className="ion-align-items-center">
                            <IonCol size="12" sizeMd="6">
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "1rem",
                                }}
                              >
                                <div>
                                  <IonText>
                                    <h3
                                      style={{
                                        margin: "0 0 0.25rem 0",
                                        fontWeight: "600",
                                      }}
                                    >
                                      {user.name || "Unknown User"}
                                    </h3>
                                  </IonText>
                                  <IonText color="medium">
                                    <p
                                      style={{
                                        margin: "0",
                                        fontSize: "0.875rem",
                                      }}
                                    >
                                      {user.email || "No email provided"}
                                    </p>
                                  </IonText>
                                </div>
                              </div>
                            </IonCol>
                            <IonCol size="6" sizeMd="3">
                              <IonText color="medium">
                                <p
                                  style={{
                                    margin: "0 0 0.25rem 0",
                                    fontSize: "0.875rem",
                                  }}
                                >
                                  <strong>Phone:</strong> {user.phone || "N/A"}
                                </p>
                                <p
                                  style={{ margin: "0", fontSize: "0.875rem" }}
                                >
                                  <strong>User ID:</strong>{" "}
                                  {user.user_id || "N/A"}
                                </p>
                              </IonText>
                            </IonCol>
                            <IonCol
                              size="6"
                              sizeMd="3"
                              className="ion-text-end"
                            >
                              <IonChip color="primary" style={{ margin: 0 }}>
                                <IonLabel style={{ fontSize: "0.75rem" }}>
                                  {formatDate(user.created_at)}
                                </IonLabel>
                              </IonChip>
                            </IonCol>
                          </IonRow>
                        </IonGrid>
                      </div>
                    </IonItem>
                  ))}
                </IonList>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginTop: "1.5rem",
                      padding: "1rem 0.5rem",
                    }}
                  >
                    <IonButton
                      fill="clear"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(currentPage - 1)}
                    >
                      Previous
                    </IonButton>

                    <IonText color="medium">
                      Page {currentPage} of {totalPages}
                    </IonText>

                    <IonButton
                      fill="clear"
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(currentPage + 1)}
                    >
                      Next
                    </IonButton>
                  </div>
                )}
              </IonCardContent>
            </IonCard>
          </>
        )}
      </IonContent>
    </IonPage>
  );
};

export default AnalyticsDashboard;
